export interface Options {
  enableAutoReload?: boolean | undefined;
  rootMargin?: string | undefined;
  threshold?: number | undefined;
  load?(element: Element): void;
  loaded?(element: Element): void;
}

/**
 *
 * @param {string} type
 *
 */
const support = (type: string) => window && window[type as any]

const validAttribute = ['data-iesrc', 'data-alt', 'data-src', 'data-srcset', 'data-background-image', 'data-toggle-class']

const defaultConfig: Options = {
  rootMargin: '0px',
  threshold: 0,
  enableAutoReload: false,
  load(element: any) {
    if (element.nodeName.toLowerCase() === 'picture') {
      let img = element.querySelector('img')
      let append = false

      if (img === null) {
        img = document.createElement('img')
        append = true
      }

      if (element.getAttribute('data-alt')) {
        img.alt = element.getAttribute('data-alt');
      }

      if (append) {
        element.append(img)
      }
    }

    if (element.nodeName.toLowerCase() === 'video' && !element.getAttribute('data-src')) {
      if (element.children) {
        const childs = element.children
        let childSrc
        for (let i = 0; i <= childs.length - 1; i++) {
          childSrc = childs[i].getAttribute('data-src')
          if (childSrc) {
            childs[i].src = childSrc
          }
        }

        element.load()
      }
    }

    if (element.getAttribute('data-poster')) {
      element.poster = element.getAttribute('data-poster')
    }

    if (element.getAttribute('data-src')) {
      element.src = element.getAttribute('data-src')
    }

    if (element.getAttribute('data-srcset')) {
      element.setAttribute('srcset', element.getAttribute('data-srcset'))
    }

    let backgroundImageDelimiter = ','
    if (element.getAttribute('data-background-delimiter')) {
      backgroundImageDelimiter = element.getAttribute('data-background-delimiter')
    }

    if (element.getAttribute('data-background-image')) {
      element.style.backgroundImage = `url('${element.getAttribute('data-background-image').split(backgroundImageDelimiter).join('\'),url(\'')}')`
    } else if (element.getAttribute('data-background-image-set')) {
      const imageSetLinks = element.getAttribute('data-background-image-set').split(backgroundImageDelimiter)
      let firstUrlLink = (imageSetLinks[0].substr(0, imageSetLinks[0].indexOf(' ')) || imageSetLinks[0]) // Substring before ... 1x
      firstUrlLink = firstUrlLink.indexOf('url(') === -1 ? `url(${firstUrlLink})` : firstUrlLink
      if (imageSetLinks.length === 1) {
        element.style.backgroundImage = firstUrlLink
      } else {
        element.setAttribute('style', (element.getAttribute('style') || '') + `background-image: ${firstUrlLink}; background-image: -webkit-image-set(${imageSetLinks}); background-image: image-set(${imageSetLinks})`)
      }
    }

    if (element.getAttribute('data-toggle-class')) {
      element.classList.toggle(element.getAttribute('data-toggle-class'))
    }
  },
  loaded() {},
}

function markAsLoaded(element: Element) {
  element.setAttribute('data-loaded', 'true')
}

function preLoad(element: any) {
  if (element.getAttribute('data-placeholder-background')) {
    element.style.background = element.getAttribute('data-placeholder-background')
  }
}

const isLoaded = (element: Element) => element.getAttribute('data-loaded') === 'true'

const onIntersection = (load: any, loaded: any) => (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
  entries.forEach(entry => {
    if (entry.intersectionRatio > 0 || entry.isIntersecting) {
      observer.unobserve(entry.target)

      if (!isLoaded(entry.target)) {
        load(entry.target)
        markAsLoaded(entry.target)
        loaded(entry.target)
      }
    }
  })
}

const onMutation = (load: any) => (entries: MutationRecord[]) => {
  entries.forEach(entry => {
    if (isLoaded(entry.target as Element) && entry.type === 'attributes' && validAttribute.indexOf(entry.attributeName as string) > -1) {
      load(entry.target)
    }
  })
}

const getElements = (selector: Element | NodeList | string, root = document) => {
  if (selector instanceof Element) {
    return [selector]
  }

  if (selector instanceof NodeList) {
    return selector
  }

  return root.querySelectorAll(selector)
}

export default function(selector = '.lozad', options = {}) {
  const { rootMargin, threshold, enableAutoReload, load, loaded } = Object.assign({}, defaultConfig, options)
  let observer: IntersectionObserver | null = null
  let mutationObserver: MutationObserver | null = null

  if (support('IntersectionObserver')) {
    observer = new IntersectionObserver(onIntersection(load, loaded), {
      root: document,
      rootMargin,
      threshold,
    })
  }

  if (support('MutationObserver') && enableAutoReload) {
    mutationObserver = new MutationObserver(onMutation(load))
  }

  const elements = getElements(selector)
  for (let i = 0; i < elements.length; i++) {
    preLoad(elements[i])
  }

  // MutationObserver to watch for new elements added to the DOM
  if (support('MutationObserver')) {
    const globalMutationObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) {
            let elements: Element[] = [];

            if ((node as Element).classList.contains('lozad')) {
              elements = [node as Element];
            }
            else {
              elements = (node as Element).querySelectorAll(selector) as unknown as Element[];
            }

            for (let i = 0; i < elements.length; i++) {
              if ((elements[i] as any).__lazyObserved) {
                continue;
              }

              (elements[i] as any).__lazyObserved = true

              if (observer) {
                if (mutationObserver && enableAutoReload) {
                  mutationObserver.observe(elements[i], { subtree: true, attributes: true, attributeFilter: validAttribute })
                }

                observer.observe(elements[i])
              }
              else {
                load && load(elements[i])
                markAsLoaded(elements[i])
                loaded && loaded(elements[i])
              }
            }
          }
        })
      })
    })

    globalMutationObserver.observe(document.body, { childList: true, subtree: true })
  }

  return {
    observe() {
      const elements = getElements(selector)

      for (let i = 0; i < elements.length; i++) {
        if (isLoaded(elements[i] as Element) || (elements[i] as any).__lazyObserved) {
          continue
        }

        (elements[i] as any).__lazyObserved = true  // Mark the node as observed

        if (observer) {
          if (mutationObserver && enableAutoReload) {
            mutationObserver.observe(elements[i], { subtree: true, attributes: true, attributeFilter: validAttribute })
          }

          observer.observe(elements[i] as Element)
          continue
        }

        load && load(elements[i] as Element)
        markAsLoaded(elements[i] as Element)
        loaded && loaded(elements[i] as Element)
      }
    },
    triggerLoad(element: Element) {
      if (isLoaded(element)) {
        return
      }

      load && load(element)
      markAsLoaded(element)
      loaded && loaded(element)
    },
    observer,
    mutationObserver,
  }
}
